/**
 * Destinatarios del envío de un EP u OC (SOU-544, ticket #2601).
 *
 * Al pulsar «Enviar», finance propone destinatarios (Para: receptor del PDF; CC: los
 * titulares de los roles del proyecto) y la persona los edita antes de enviar. La lista
 * editada viaja en el PATCH a `Sent` y el documento la guarda en `sentRecipients`.
 *
 * Contrato de Leonardo del 15-10 en SOU-544. `kind` se documentó como `To` (1) / `Cc` (2):
 * la web ENVÍA texto y al LEER acepta las dos formas, hasta que se confirme cuál serializa
 * la API.
 */

export type OrderRecipientKind = 'To' | 'Cc';

/** Un destinatario tal y como se edita en la ventana y se manda en el PATCH. */
export interface OrderRecipient {
  email: string;
  name?: string | null;
  kind: OrderRecipientKind;
  /** Vacío cuando la dirección se añadió a mano. */
  partnerId?: number | null;
  /** Por qué lo propone el sistema («Receptor del documento», «Jefe de Operaciones»...). */
  reason?: string | null;
}

/** Respuesta de `send-proposal`. */
export interface OrderSendProposal {
  orderId: number;
  currencyId: number;
  recipients: OrderRecipient[];
}

/** Un destinatario ya enviado, tal y como lo devuelve el DTO del EP/OC. */
export interface OrderSentRecipient {
  email: string;
  name?: string | null;
  kind: OrderRecipientKind;
  partnerId?: number | null;
  currencyId: number;
  sentOn: string;
  sentById?: number | null;
}

// Misma forma práctica que valida el navegador en <input type="email">: algo@dominio.tld
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidRecipientEmail(email: string | null | undefined): boolean {
  return EMAIL_PATTERN.test((email ?? '').trim());
}

/** Acepta `To`/`Cc` en cualquier capitalización o 1/2. Cualquier otra cosa cuenta como copia. */
export function normalizeRecipientKind(kind: unknown): OrderRecipientKind {
  if (kind === 1 || kind === '1') return 'To';
  if (typeof kind === 'string' && kind.toLowerCase() === 'to') return 'To';
  return 'Cc';
}

function sameEmail(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Normaliza la propuesta: `kind` en texto, correos sin espacios y sin repetidos (gana el primero). */
export function normalizeRecipients(recipients: any[] | null | undefined): OrderRecipient[] {
  const result: OrderRecipient[] = [];
  (recipients ?? []).forEach(r => {
    const email = (r?.email ?? '').trim();
    if (!email || result.some(x => sameEmail(x.email, email))) return;
    result.push({
      email,
      name: r?.name ?? null,
      kind: normalizeRecipientKind(r?.kind),
      partnerId: r?.partnerId ?? null,
      reason: r?.reason ?? null,
    });
  });
  return result;
}

/**
 * Lo que impide enviar la lista, con el mismo criterio que el 400 de finance. Vacío si se
 * puede enviar.
 */
export function getRecipientsBlockers(recipients: OrderRecipient[]): string[] {
  const blockers: string[] = [];
  if (!recipients.some(r => r.kind === 'To')) {
    blockers.push('Hace falta al menos un destinatario en «Para».');
  }
  // Mismos textos que el 400 de finance, para que el aviso sea igual lo detecte quien lo detecte
  recipients
    .filter(r => !isValidRecipientEmail(r.email))
    .forEach(r => blockers.push(`La dirección «${r.email}» no es un correo válido.`));
  return blockers;
}

/** Añade una dirección a mano. Devuelve el motivo si no se puede, o null si se añadió. */
export function addManualRecipient(recipients: OrderRecipient[], email: string, kind: OrderRecipientKind): string | null {
  const clean = (email ?? '').trim();
  if (!isValidRecipientEmail(clean)) return 'Escribe un correo válido.';
  if (recipients.some(r => sameEmail(r.email, clean))) return 'Ese correo ya está en la lista.';
  recipients.push({email: clean, kind, partnerId: null, name: null, reason: null});
  return null;
}

/** Cuerpo de `recipients` para el PATCH: sin `reason`, que es solo informativo. */
export function toRecipientsPayload(recipients: OrderRecipient[]): Omit<OrderRecipient, 'reason'>[] {
  return recipients.map(r => {
    const item: Omit<OrderRecipient, 'reason'> = {email: r.email.trim(), kind: r.kind};
    if (r.name) item.name = r.name;
    if (r.partnerId) item.partnerId = r.partnerId;
    return item;
  });
}

/**
 * «Enviado a»: el envío de `sentOn` más reciente para esa moneda. Finance sella todas las filas
 * de un envío con el mismo instante (el ShipperDate del paso a Sent), así que la fecha identifica
 * el envío; si se reenvió, los destinatarios de envíos anteriores no cuentan.
 */
export function getLatestSentRecipients(sent: any[] | null | undefined, currencyId: number): OrderSentRecipient[] {
  const ofCurrency = (sent ?? []).filter(r => r?.currencyId === currencyId && r?.sentOn);
  if (!ofCurrency.length) return [];
  const latest = Math.max(...ofCurrency.map(r => new Date(r.sentOn).getTime()));
  return ofCurrency
    .filter(r => new Date(r.sentOn).getTime() === latest)
    .map(r => ({...r, kind: normalizeRecipientKind(r.kind)}))
    .sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'To' ? -1 : 1));
}
